import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, Select, Space, Table, Tag } from 'antd';
import { useConnection } from '../../connection/connection';
import {
  AccountTransaction,
  useBusinessContext,
} from '../../context/BusinessContext';
import { useEventListener } from '../../common/EventContext';
import { Utils } from '../../common/Utils';
import classNames from 'classnames';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Account } from '../../dto';
import IncomesModal, { useIncomesModal } from './IncomesModal';
import dayjs from 'dayjs';
import ViewIncomeModal, { useViewIncomesModal } from './ViewIncomeModal';
import viewIncomeModal from './ViewIncomeModal';
import { ExpenseStatus } from '../../dto/Expense';

type Props = {};

export const IncomesPage: FC<Props> = (props) => {
  const connection = useConnection();
  const business = useBusinessContext();
  const [siteId, setSiteId] = useState<string>('0');
  const [accountId, setAccountId] = useState<string>('0');
  const [loading, setLoading] = useState<boolean>(false);
  const [incomes, setIncomes] = useState<any[]>([]);
  const incomeModal = useIncomesModal();
  const incomeViewRef = useViewIncomesModal();

  useEventListener(
    'INCOMES',
    useCallback((evt) => {
      if (evt.action === 'CREATE') {
        setIncomes((prevState) => {
          if (!prevState.some((income) => income.id === evt.data.id)) {
            return [...prevState, evt.data];
          }
          return prevState;
        });
      } else if (evt.action === 'UPDATE') {
        setIncomes((prevState) =>
          prevState.map((s) => (s.id === evt.data.id ? evt.data : s)),
        );
      } else {
        setIncomes((prevState) =>
          prevState.filter((s) => s.id !== evt.data.id),
        );
      }
    }, []),
  );

  useEffect(() => {
    setLoading(true);
    connection
      .get(`incomes/${business.businessId}`)
      .then((res) => {
        setIncomes(
          res.data.incomes.sort((a: any, b: any) => {
            const dateComparison =
              new Date(b.date).getTime() - new Date(a.date).getTime();
            return dateComparison === 0
              ? b.createdAt - a.createdAt
              : dateComparison;
          }),
        );
        // set(res.data.lastKey);
      })
      .finally(() => setLoading(false));
  }, [accountId, business.businessId, connection, siteId]);

  const columns: any = useMemo(
    () => [
      {
        key: 'date',
        title: 'Date',
        dataIndex: 'date',
        render: (_: any, record: any) => (
          <span
            className={'cursor-pointer text-green-600'}
            onClick={() => {
              incomeViewRef.current?.openTx(record.id);
            }}
          >
            {record.date}
          </span>
        ),
      },
      {
        key: 'site',
        title: 'Site',
        dataIndex: 'siteId',
        render: (site: string, record: any) => business.sites[site].name,
      },
      {
        key: 'account',
        title: 'Account',
        dataIndex: 'account',
        render: (_: any, record: any) => (
          <span>{business.accounts[record.account]?.name}</span>
        ),
      },
      {
        key: 'category',
        title: 'Category',
        dataIndex: 'category',
        render: (text: string) => business.incomeTypes[text].name,
      },
      {
        key: 'refNo',
        title: 'Ref Number',
        dataIndex: 'refNo',
      },
      {
        key: 'description',
        title: 'Description',
        dataIndex: 'description',
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (status: string, record: any) =>
          status === undefined ? (
            <Tag color={'blue'}>OPEN</Tag>
          ) : (
            <Tag color={business.states[status]?.color}>
              {business.states[status]?.name}
            </Tag>
          ),
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        align: 'end',
        render: (text: number, record: any) => (
          <span className={'text-green-600'}>{Utils.formatPrice(text)}</span>
        ),
      },
      {
        key: 'actions',
        title: 'Actions',
        dataIndex: 'hk',
        render: (text: string, record: any) => (
          <Space>
            {record.status === undefined && (
              <Button
                type="link"
                onClick={() => {
                  incomeModal.current?.openTx(record.id);
                }}
              >
                <EditOutlined />
              </Button>
            )}
            <Button
              type="link"
              onClick={() => {
                incomeViewRef.current?.openTx(record.id);
              }}
            >
              <EyeOutlined />
            </Button>
          </Space>
        ),
      },
    ],
    [
      business.accounts,
      business.incomeTypes,
      business.sites,
      business.states,
      incomeModal,
      incomeViewRef,
    ],
  );

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>Income</WebUxTitle>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 justify-between w-full">
          <Space>
            <Select
              className="w-[150px]"
              options={[
                { label: 'All sites', value: '0' },
                { label: 'Sites', options: business.select.sites },
              ]}
              value={siteId}
              onChange={(site) => {
                setSiteId(site);
                setAccountId('0');
              }}
            />
          </Space>
          <Space>
            <Button
              type={'primary'}
              onClick={() => {
                incomeModal.current?.open(
                  siteId === '0' ? undefined : siteId,
                  accountId === '0' ? undefined : accountId,
                );
              }}
            >
              Create Income
            </Button>
          </Space>
        </div>
      </div>
      <Table
        size="small"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={incomes}
      />
      {/*<div className="flex justify-center gap-2">*/}
      {/*  <Button disabled={!lastKey} onClick={loadMore}>*/}
      {/*    Load more transactions*/}
      {/*  </Button>*/}
      {/*</div>*/}
      <IncomesModal ref={incomeModal} />
      <ViewIncomeModal ref={incomeViewRef} />
    </div>
  );
};
